import JsBarcode from "jsbarcode"
import { useEffect, useRef } from "react"

const DEFAULT_HEIGHT = 48
const DEFAULT_BAR_WIDTH = 2
const DEFAULT_MAX_WIDTH = 320
const BARCODE_FORMAT = "CODE128"

const renderBarcode = (
  svg: SVGSVGElement,
  value: string,
  height: number,
  barWidth: number,
) => {
  JsBarcode(svg, value, {
    format: BARCODE_FORMAT,
    displayValue: false,
    height,
    width: barWidth,
    margin: 0,
  })
}

export const isBarcodeValueValid = (value: string) => {
  try {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    renderBarcode(svg, value, DEFAULT_HEIGHT, DEFAULT_BAR_WIDTH)
    return true
  } catch {
    return false
  }
}

const ensureViewBox = (svg: SVGSVGElement) => {
  if (svg.getAttribute("viewBox")) return
  const width = svg.getAttribute("width")
  const height = svg.getAttribute("height")
  if (width && height) {
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`)
  }
}

const applySizing = (svg: SVGSVGElement, height: number, maxWidth?: number) => {
  svg.setAttribute("height", String(height))
  if (maxWidth) {
    svg.setAttribute("width", String(maxWidth))
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet")
  }
}

export const buildBarcodeSvgMarkup = (
  value: string,
  options?: { height?: number; barWidth?: number; maxWidth?: number },
) => {
  const height = options?.height ?? DEFAULT_HEIGHT
  const barWidth = options?.barWidth ?? DEFAULT_BAR_WIDTH
  const maxWidth = options?.maxWidth ?? DEFAULT_MAX_WIDTH
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")

  renderBarcode(svg, value, height, barWidth)

  ensureViewBox(svg)
  applySizing(svg, height, maxWidth)

  return svg.outerHTML
}

export const BarcodeSvg = ({
  value,
  maxWidth = DEFAULT_MAX_WIDTH,
  height = DEFAULT_HEIGHT,
  barWidth = DEFAULT_BAR_WIDTH,
}: {
  value: string
  maxWidth?: number
  /** Barcode bar height in pixels. Defaults to 48. */
  height?: number
  /** Width of a single bar in pixels. Defaults to 2. */
  barWidth?: number
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null)

  useEffect(() => {
    if (!svgRef.current) return
    try {
      renderBarcode(svgRef.current, value, height, barWidth)
      ensureViewBox(svgRef.current)
      applySizing(svgRef.current, height, maxWidth)
      svgRef.current.removeAttribute("data-invalid")
    } catch {
      svgRef.current.innerHTML = ""
      svgRef.current.setAttribute("data-invalid", "true")
    }
  }, [value, maxWidth, height, barWidth])

  return <svg ref={svgRef} role="img" aria-label="Barcode" />
}
